import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';
import { DatasourceColumnDataLoaderFactoryOptions } from '../../common/data-loaders/datasource-column';
import { IQueryContextDatasourceColumn } from 'src/app/services/query-context/models/IQueryContext';
import { DatasourceColumnHelper } from '../../common/data-loaders/datasource-column-helper';

export interface TableUsersDataLoaderFactoryOptions
    extends
        ICreatedInstanceId,
        IGcpProjectId,
        IQueryCacheService,
        Pick<
            DatasourceColumnDataLoaderFactoryOptions,
            'filterContext' | 'ignoredContextColumnIds'
        > {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class TableUsersDataLoaderFactory extends Factory<
    DataLoader,
    TableUsersDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'TableUsersDataLoaderFactory';

    getInstance(options: TableUsersDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardUsersActivity.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                // context filter functionality
                dsColumns.present = dsColumns.present.filter(
                    (col: IQueryContextDatasourceColumn) => {
                        return DatasourceColumnHelper.filterContextFn(
                            col,
                            options
                        );
                    }
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const {
                    dateTrunc,
                    splitSelectAsSeparateColumns,
                    splitGroupByTable,
                    where,
                    // is_in_context_app_version,
                    // unnestDatasourceColumn,
                    join,
                } = BiVizDashboardUsersActivity.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    options
                    // selectedDayNoSQL
                );

                // const selectFacts = is_in_context_app_version
                //     ? 'ds.date, ds.proper_id, app_version.*,'
                //     : 'ds.*,';

                const query = `
-- DataLoaderId: ${this.instanceId}

SELECT
  player_dim.device.first_device.unified_brand AS first_device_brand
  , player_dim.device.first_device.model AS first_device_model
  , COUNT(DISTINCT player_id) AS users
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}\` ds
${join}
${where}
GROUP BY first_device_brand, first_device_model
ORDER BY users DESC
LIMIT 200
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
